import { type QueryClient, useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { search, searchQueryKey } from '@cfa/pathway-fe-shared/xp';

import { buildExploreSearchViewModel } from '@/hooks/buildExploreSearchViewModel';
import { trackExploreSearchResult } from '@/hooks/exploreSearchAnalytics';
import { useSmartSearchEnabled } from '@/hooks/useSmartSearchEnabled';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import { userPrefsAtom } from '@/atoms/userPrefs';

export type {
  ExploreSearchPage,
  ExploreSearchViewModel,
} from '@/hooks/buildExploreSearchViewModel';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Matches only the XP search query for a specific search term. Search keys are
 * shaped `[{ _id: 'search', query: { ..., query: <text> } }]`.
 */
export const isSearchQueryKeyForTerm = (
  queryKey: readonly unknown[],
  term: string,
): boolean => {
  const [key] = queryKey;
  if (!isRecord(key) || key._id !== 'search') return false;
  const params = key.query;
  return isRecord(params) && params.query === term;
};

/** Drop cached search query for one term (history reselect / Enter). */
export const removeSearchCacheForTerm = (
  queryClient: QueryClient,
  term: string,
): void => {
  queryClient.removeQueries({
    predicate: q => isSearchQueryKeyForTerm(q.queryKey, term),
  });
};

/**
 * Explore search: one GET /search (page 1), client dedupe.
 *
 * @todo pathway-xp-api: Use deduped GET /search responses; drop client consolidation
 *   when XP ships (PATHWAY-4127).
 */
const useExploreSearch = (query: string) => {
  const { country: selectedCountry, language: languageCode } =
    useAtomValue(userPrefsAtom);

  const track = useAmplitudeTrack();
  const smartSearchEnabled = useSmartSearchEnabled();
  const { aiSearchPocQueryParameters } = useFlags();

  const smartSearchActive = smartSearchEnabled && query.split(' ').length > 2;

  const baseSearchParams = {
    country: selectedCountry.id ?? '',
    language: languageCode,
    query,
    ...(smartSearchActive ? aiSearchPocQueryParameters : {}),
  };

  const queryResult = useQuery({
    queryKey: searchQueryKey({ query: baseSearchParams }),
    queryFn: async () => {
      const result = await search({
        query: {
          ...baseSearchParams,
          pageNumber: 1,
        },
      });
      const response = result.data;

      if (response) {
        trackExploreSearchResult({
          track,
          query: baseSearchParams.query,
          queryId: response.queryId,
          numResults: response.totalResults,
          smartSearchEnabled,
        });
      }

      return response;
    },
    enabled: !!query && !!aiSearchPocQueryParameters,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    select: buildExploreSearchViewModel,
  });

  return queryResult;
};

export default useExploreSearch;
